/* AUTO-SYNCED from PsyKit.io/components/bilateral/ControlPanel.tsx — DO NOT EDIT. Regenerate with `npm run sync`. */
"use client";

import { useRef, useCallback, useEffect, useState } from "react";
import { AUDIO_PRESETS, type AudioPreset } from "@/components/audio/AudioEngine";
import { SOUND_LIBRARY, type UserAudioFile } from "@/components/soundboard/types";
import HapticsStatus from "@/components/bilateral/HapticsStatus";
import type { HapticsStatus as HapticsStatusType } from "@/lib/haptics/useGamepadHaptics";

interface ControlPanelProps {
  /** Tempo in true Hz. 1 cycle = left → right → left. Range: 0.5–2.5. */
  hz: number;
  setHz: (v: number) => void;
  ballSize: number;
  setBallSize: (v: number) => void;
  ballColor: string;
  setBallColor: (v: string) => void;
  canvasBg: string;
  setCanvasBg: (v: string) => void;
  isRunning: boolean;
  isStopping: boolean;
  onStart: () => void;
  onStop: () => void;
  onReset: () => void;
  passCount: number;
  passLimit: number;
  setPassLimit: (v: number) => void;
  passLimitOn: boolean;
  setPassLimitOn: (v: boolean) => void;
  timerOn: boolean;
  setTimerOn: (v: boolean) => void;
  timerDuration: number;
  setTimerDuration: (v: number) => void;
  timerDisplay: string;
  timerExpiring: boolean;
  interfaceTheme: string;
  setInterfaceTheme: (v: string) => void;
  // Audio
  audioOn: boolean;
  setAudioOn: (v: boolean) => void;
  audioVolume: number;
  setAudioVolume: (v: number) => void;
  audioPreset: string;
  setAudioPreset: (v: string) => void;
  /** Sound IDs shown as preset buttons: "theta"|"alpha"|"delta", SOUND_LIBRARY ids, or "custom:<uuid>". */
  audioPresetIds?: string[];
  /** Called when the therapist changes the visible preset shortlist. */
  onAudioPresetIdsChange?: (ids: string[]) => void;
  /** User's uploaded audio files — shown in the configure panel. */
  userAudioFiles?: UserAudioFile[];
  /** When true, hides the inline countdown timer block — used by the
   *  redesigned session view, which renders the timer inside the floating
   *  LivePreview widget instead. Pass counter still renders. */
  hideTimer?: boolean;
  // Haptics — controller rumble pulses synced to ball edges. Free and
  // unlimited on every tier.
  hapticsOn: boolean;
  setHapticsOn: (v: boolean) => void;
  hapticsIntensity: number;
  setHapticsIntensity: (v: number) => void;
  /** Status from useGamepadHaptics — drives the inline badge. */
  hapticsStatus: HapticsStatusType;
  /** Cleaned controller id, or null when no controller is exposed. */
  hapticsControllerLabel: string | null;
}

// ── Ball colors: original muted set + new flat/matte options ──
const BALL_COLORS = [
  { color: "#4a6fa5", label: "Slate Blue" },
  { color: "#5a8a6a", label: "Sage" },
  { color: "#8a6a5a", label: "Terracotta" },
  { color: "#7a6a8a", label: "Mauve" },
  { color: "#5a7a8a", label: "Steel" },
  { color: "#8a7a5a", label: "Warm Tan" },
  { color: "#c0bdb8", label: "Silver" },
  { color: "#a0c4c8", label: "Aqua" },
  { color: "#c8a0b4", label: "Blush" },
  { color: "#e8e4df", label: "Linen" },
  // New flat/matte options (less glowy)
  { color: "#3d4f5f", label: "Charcoal Teal" },
  { color: "#5c5c5c", label: "Graphite" },
  { color: "#6b5b4e", label: "Driftwood" },
  { color: "#4a5a4a", label: "Moss" },
  { color: "#7b6e82", label: "Dusty Plum" },
  { color: "#8c8478", label: "Sandstone" },
  { color: "#445566", label: "Slate" },
  { color: "#f5f0e6", label: "Cream" },
];

// ── Canvas backgrounds: original set + new matte options ──
const CANVAS_BGS = [
  { bg: "#1e1d1b", label: "Near Black" },
  { bg: "#111827", label: "Midnight" },
  { bg: "#1a2030", label: "Deep Navy" },
  { bg: "#1a2018", label: "Forest" },
  { bg: "#2a1f1f", label: "Dark Mahogany" },
  { bg: "#0D1B2E", label: "Psy180 Navy" },
  // New matte backgrounds
  { bg: "#0a0a0a", label: "True Black" },
  { bg: "#1c1c1e", label: "Onyx" },
  { bg: "#1a1a2e", label: "Ink" },
  { bg: "#2d2b2b", label: "Charcoal" },
  { bg: "#1f2420", label: "Dark Olive" },
  { bg: "#262226", label: "Espresso" },
  { bg: "#ffffff", label: "White" },
];

// ── Interface themes ──
const INTERFACE_THEMES = [
  {
    id: "posh-clinical",
    label: "Posh Clinical",
    panel: "#112240",
    panelBorder: "#233D6B",
    accent: "#C9A84C",
    textMuted: "#7A90A8",
    textPrimary: "#EEF2F7",
    inputBg: "#0D1B2E",
  },
  {
    id: "warm-neutral",
    label: "Warm Neutral",
    panel: "#2a2520",
    panelBorder: "#4a4035",
    accent: "#c8a97a",
    textMuted: "#a09585",
    textPrimary: "#e8e0d6",
    inputBg: "#1e1a16",
  },
  {
    id: "cool-slate",
    label: "Cool Slate",
    panel: "#1e2328",
    panelBorder: "#3a4048",
    accent: "#7aa8c4",
    textMuted: "#8a98a6",
    textPrimary: "#e0e6ec",
    inputBg: "#161a1e",
  },
  {
    id: "forest",
    label: "Forest",
    panel: "#1a231c",
    panelBorder: "#2e4032",
    accent: "#7aab7e",
    textMuted: "#82a088",
    textPrimary: "#dce8de",
    inputBg: "#141c16",
  },
  {
    id: "minimal-dark",
    label: "Minimal Dark",
    panel: "#1a1a1a",
    panelBorder: "#333333",
    accent: "#999999",
    textMuted: "#777777",
    textPrimary: "#e0e0e0",
    inputBg: "#111111",
  },
];

// ── Hold-to-repeat stepper hook (accelerating) ──
function useHoldStepper(
  fn: () => void,
  initialDelay = 400,
  minInterval = 50
) {
  const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const repeatTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef(200);
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const step = useCallback(() => {
    fnRef.current();
    intervalRef.current = Math.max(minInterval, intervalRef.current * 0.72);
    repeatTimerRef.current = setTimeout(step, intervalRef.current);
  }, [minInterval]);

  const cancel = useCallback(() => {
    if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    if (repeatTimerRef.current) clearTimeout(repeatTimerRef.current);
    holdTimerRef.current = null;
    repeatTimerRef.current = null;
    intervalRef.current = 200;
  }, []);

  const handlers = useCallback(
    () => ({
      onMouseDown: (e: React.MouseEvent) => {
        e.preventDefault();
        fnRef.current();
        holdTimerRef.current = setTimeout(step, initialDelay);
      },
      onTouchStart: (e: React.TouchEvent) => {
        e.preventDefault();
        fnRef.current();
        holdTimerRef.current = setTimeout(step, initialDelay);
      },
      onMouseUp: cancel,
      onMouseLeave: cancel,
      onTouchEnd: cancel,
      onTouchCancel: cancel,
    }),
    [step, cancel, initialDelay]
  );

  // Cleanup on unmount
  useEffect(() => cancel, [cancel]);

  return handlers;
}

// ── Stepper button component ──
function StepperButton({
  onStep,
  children,
  theme,
}: {
  onStep: () => void;
  children: React.ReactNode;
  theme: (typeof INTERFACE_THEMES)[0];
}) {
  const getHandlers = useHoldStepper(onStep);
  const h = getHandlers();

  return (
    <button
      {...h}
      className="w-5 h-5 flex items-center justify-center border rounded-[2px] text-xs transition-colors select-none"
      style={{
        color: theme.textMuted,
        borderColor: theme.panelBorder,
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.borderColor = theme.accent;
        e.currentTarget.style.color = theme.accent;
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.borderColor = theme.panelBorder;
        e.currentTarget.style.color = theme.textMuted;
      }}
    >
      {children}
    </button>
  );
}

// ── Preset button label helpers ───────────────────────────────────────────

function getPresetLabel(
  id: string,
  userAudioFiles: UserAudioFile[]
): { main: string; sub: string | null } {
  if (id === "off") return { main: "Off", sub: null };
  if (id === "theta" || id === "alpha" || id === "delta") {
    const p = AUDIO_PRESETS[id as Exclude<AudioPreset, "off">];
    return { main: p.label, sub: `${p.carrier} Hz` };
  }
  if (id.startsWith("custom:")) {
    const file = userAudioFiles.find((f) => f.id === id.slice(7));
    return {
      main: file?.display_name?.slice(0, 10) ?? "File",
      sub: file?.file_type?.toUpperCase() ?? null,
    };
  }
  const sound = SOUND_LIBRARY.find((s) => s.id === id);
  if (!sound) return { main: id.slice(0, 10), sub: null };
  const name = sound.name
    .replace(" Binaural Beat", "")
    .replace(" Isochronic Tone", " Iso")
    .replace(" — ", " ")
    .replace("'Natural' ", "")
    .replace("Standard ", "")
    .slice(0, 11);
  return {
    main: name,
    sub: sound.carrierHz ? `${sound.carrierHz} Hz` : sound.type,
  };
}

function getAudioDesc(id: string, userAudioFiles: UserAudioFile[]): string {
  if (id === "off") return "";
  if (id === "theta" || id === "alpha" || id === "delta") {
    return AUDIO_PRESETS[id as Exclude<AudioPreset, "off">].desc;
  }
  if (id.startsWith("custom:")) {
    const file = userAudioFiles.find((f) => f.id === id.slice(7));
    return file?.display_name ?? "Custom audio";
  }
  const sound = SOUND_LIBRARY.find((s) => s.id === id);
  if (!sound) return id;
  return [
    sound.name,
    sound.carrierHz && `${sound.carrierHz} Hz`,
    sound.beatHz && `${sound.beatHz} Hz beat`,
  ]
    .filter(Boolean)
    .join(" · ");
}

export default function ControlPanel({
  hz,
  setHz,
  ballSize,
  setBallSize,
  ballColor,
  setBallColor,
  canvasBg,
  setCanvasBg,
  isRunning,
  isStopping,
  onStart,
  onStop,
  onReset,
  passCount,
  passLimit,
  setPassLimit,
  passLimitOn,
  setPassLimitOn,
  timerOn,
  setTimerOn,
  timerDuration,
  setTimerDuration,
  timerDisplay,
  timerExpiring,
  interfaceTheme,
  setInterfaceTheme,
  audioOn,
  setAudioOn,
  audioVolume,
  setAudioVolume,
  audioPreset,
  setAudioPreset,
  audioPresetIds = ["theta", "alpha", "delta"],
  onAudioPresetIdsChange,
  userAudioFiles = [],
  hideTimer = false,
  hapticsOn,
  setHapticsOn,
  hapticsIntensity,
  setHapticsIntensity,
  hapticsStatus,
  hapticsControllerLabel,
}: ControlPanelProps) {
  const theme =
    INTERFACE_THEMES.find((t) => t.id === interfaceTheme) ??
    INTERFACE_THEMES[0];

  const [showPresetConfig, setShowPresetConfig] = useState(false);

  return (
    <div
      className="rounded-[4px] p-5 animate-fade-in-up transition-colors duration-300"
      style={{
        background: theme.panel,
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: theme.panelBorder,
      }}
    >
      {/* Tempo & Size sliders */}
      <div className="grid grid-cols-2 gap-5 mb-5">
        {/* Tempo — true Hz: 1 cycle = left → right → left */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <label
              className="font-[var(--font-barlow)] text-[11px] font-medium uppercase tracking-[0.1em]"
              style={{ color: theme.textMuted }}
            >
              Tempo
            </label>
            <span
              className="font-[var(--font-dm-mono)] text-[11px] font-medium"
              style={{ color: theme.accent }}
            >
              {hz.toFixed(1)} Hz
            </span>
          </div>
          <input
            type="range"
            min="0.5"
            max="2.5"
            step="0.1"
            value={hz}
            onChange={(e) => setHz(parseFloat(e.target.value))}
            className="w-full"
          />
        </div>

        {/* Ball Size */}
        <div className="flex flex-col gap-2">
          <div className="flex justify-between items-center">
            <label
              className="font-[var(--font-barlow)] text-[11px] font-medium uppercase tracking-[0.1em]"
              style={{ color: theme.textMuted }}
            >
              Ball Size
            </label>
            <span
              className="font-[var(--font-dm-mono)] text-[11px] font-medium"
              style={{ color: theme.accent }}
            >
              {ballSize}px
            </span>
          </div>
          <input
            type="range"
            min="15"
            max="80"
            step="5"
            value={ballSize}
            onChange={(e) => setBallSize(parseInt(e.target.value))}
            className="w-full"
          />
        </div>
      </div>

      {/* Pass counter & Timer */}
      <div className={`grid gap-3 mb-5 ${hideTimer ? "grid-cols-1" : "grid-cols-2"}`}>
        {/* Pass Counter */}
        <div
          className="flex flex-col gap-2 rounded-[4px] px-4 py-3"
          style={{
            background: theme.inputBg,
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: theme.panelBorder,
          }}
        >
          {/* Row 1: label + count */}
          <div className="flex items-center justify-between">
            <span
              className="font-[var(--font-barlow)] text-[12px] font-medium uppercase tracking-[0.1em]"
              style={{ color: theme.textMuted }}
            >
              Passes
            </span>
            <span
              className="font-[var(--font-dm-mono)] text-xl font-medium"
              style={{
                color:
                  passLimitOn && passCount >= passLimit
                    ? "#8B2E2E"
                    : theme.accent,
              }}
            >
              {passLimitOn ? `${passCount} / ${passLimit}` : passCount}
            </span>
          </div>
          {/* Row 2: toggle + steppers */}
          <div className="flex items-center gap-1.5">
            <label className="flex items-center gap-1 cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                checked={passLimitOn}
                onChange={(e) => setPassLimitOn(e.target.checked)}
                className="sr-only"
              />
              <div
                className="w-7 h-4 rounded-full transition-colors relative"
                style={{
                  background: passLimitOn
                    ? theme.accent
                    : theme.panelBorder,
                }}
              >
                <div
                  className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                    passLimitOn ? "translate-x-3.5" : "translate-x-0.5"
                  }`}
                />
              </div>
              <span
                className="font-[var(--font-dm-mono)] text-[10px]"
                style={{ color: theme.textMuted }}
              >
                Limit
              </span>
            </label>
            {passLimitOn && (
              <div className="flex items-center gap-1 flex-1 justify-end">
                <StepperButton
                  onStep={() =>
                    setPassLimit(Math.max(1, passLimit - 1))
                  }
                  theme={theme}
                >
                  -
                </StepperButton>
                <span
                  className="font-[var(--font-dm-mono)] text-[13px] min-w-[28px] text-center"
                  style={{ color: theme.textPrimary }}
                >
                  {passLimit}
                </span>
                <StepperButton
                  onStep={() =>
                    setPassLimit(Math.min(200, passLimit + 1))
                  }
                  theme={theme}
                >
                  +
                </StepperButton>
              </div>
            )}
          </div>
        </div>

        {/* Timer — hidden when the parent renders it elsewhere (e.g. inside
            the floating LivePreview widget on the redesigned session view). */}
        {!hideTimer && (
        <div
          className="flex flex-col gap-2 rounded-[4px] px-4 py-3"
          style={{
            background: theme.inputBg,
            borderWidth: 1,
            borderStyle: "solid",
            borderColor: theme.panelBorder,
          }}
        >
          {/* Row 1: label + display */}
          <div className="flex items-center justify-between">
            <span
              className="font-[var(--font-barlow)] text-[12px] font-medium uppercase tracking-[0.1em]"
              style={{ color: theme.textMuted }}
            >
              Timer
            </span>
            <span
              className="font-[var(--font-dm-mono)] text-xl font-medium"
              style={{
                color: timerExpiring ? "#8B2E2E" : theme.accent,
              }}
            >
              {timerDisplay}
            </span>
          </div>
          {/* Row 2: toggle + steppers */}
          <div className="flex items-center gap-1.5">
            <label className="flex items-center gap-1 cursor-pointer flex-shrink-0">
              <input
                type="checkbox"
                checked={timerOn}
                onChange={(e) => setTimerOn(e.target.checked)}
                className="sr-only"
              />
              <div
                className="w-7 h-4 rounded-full transition-colors relative"
                style={{
                  background: timerOn ? theme.accent : theme.panelBorder,
                }}
              >
                <div
                  className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition-transform ${
                    timerOn ? "translate-x-3.5" : "translate-x-0.5"
                  }`}
                />
              </div>
            </label>
            {timerOn && (
              <div className="flex items-center gap-1 flex-1 justify-end">
                <StepperButton
                  onStep={() =>
                    setTimerDuration(Math.max(30, timerDuration - 30))
                  }
                  theme={theme}
                >
                  -
                </StepperButton>
                <span
                  className="font-[var(--font-dm-mono)] text-[13px] min-w-[36px] text-center"
                  style={{ color: theme.textPrimary }}
                >
                  {Math.floor(timerDuration / 60)}:{String(timerDuration % 60).padStart(2, "0")}
                </span>
                <StepperButton
                  onStep={() =>
                    setTimerDuration(Math.min(1800, timerDuration + 30))
                  }
                  theme={theme}
                >
                  +
                </StepperButton>
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Action Buttons — right below Time & Passes */}
      <div className="flex gap-2 mt-3">
        <button
          onClick={onStart}
          disabled={isRunning || isStopping}
          className="flex-1 py-2.5 rounded-[4px] font-[var(--font-barlow)] text-xs uppercase tracking-[0.1em] font-medium transition-colors bg-[#4a7c59] text-white hover:bg-[#3d6a4b] disabled:opacity-35 disabled:cursor-not-allowed"
        >
          Start
        </button>
        <button
          onClick={onStop}
          disabled={!isRunning || isStopping}
          className="flex-1 py-2.5 rounded-[4px] font-[var(--font-barlow)] text-xs uppercase tracking-[0.1em] font-medium transition-colors bg-[#8B2E2E] text-white hover:bg-[#7a2626] disabled:opacity-35 disabled:cursor-not-allowed"
        >
          Stop
        </button>
        <button
          onClick={onReset}
          className="flex-1 py-2.5 rounded-[4px] font-[var(--font-barlow)] text-xs uppercase tracking-[0.1em] font-medium transition-colors border text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
          style={{ borderColor: theme.panelBorder }}
        >
          Reset
        </button>
      </div>

      {/* Divider */}
      <div
        className="h-px my-5 transition-colors"
        style={{ background: theme.panelBorder }}
      />

      {/* Ball Color swatches — full palette on every tier. */}
      <div className="mb-4">
        <label
          className="font-[var(--font-barlow)] text-[11px] font-medium uppercase tracking-[0.1em] mb-2 block"
          style={{ color: theme.textMuted }}
        >
          Ball Color
        </label>
        <div className="flex gap-2 flex-wrap">
          {BALL_COLORS.map((s) => (
            <button
              key={s.color}
              title={s.label}
              onClick={() => setBallColor(s.color)}
              className="relative w-7 h-7 rounded-[4px] border-2 transition-transform flex-shrink-0 hover:scale-110"
              style={{
                backgroundColor: s.color,
                borderColor:
                  ballColor === s.color ? theme.accent : theme.panelBorder,
                boxShadow:
                  ballColor === s.color
                    ? `0 0 0 2px ${theme.panel}, 0 0 0 4px ${theme.accent}`
                    : "none",
                cursor: "pointer",
              }}
            />
          ))}
        </div>
      </div>

      {/* Canvas Background swatches — full palette on every tier. */}
      <div className="mb-5">
        <label
          className="font-[var(--font-barlow)] text-[11px] font-medium uppercase tracking-[0.1em] mb-2 block"
          style={{ color: theme.textMuted }}
        >
          Canvas Background
        </label>
        <div className="flex gap-2 flex-wrap">
          {CANVAS_BGS.map((s) => (
            <button
              key={s.bg}
              title={s.label}
              onClick={() => setCanvasBg(s.bg)}
              className="relative w-7 h-7 rounded-[4px] border-2 transition-transform hover:scale-110 flex-shrink-0"
              style={{
                backgroundColor: s.bg,
                borderColor:
                  canvasBg === s.bg ? theme.accent : theme.panelBorder,
                boxShadow:
                  canvasBg === s.bg
                    ? `0 0 0 2px ${theme.panel}, 0 0 0 4px ${theme.accent}`
                    : "none",
                cursor: "pointer",
              }}
            />
          ))}
        </div>
      </div>

      {/* Divider */}
      <div
        className="h-px my-5 transition-colors"
        style={{ background: theme.panelBorder }}
      />

      {/* Interface Theme selector */}
      <div className="mb-5">
        <label
          className="font-[var(--font-barlow)] text-[11px] font-medium uppercase tracking-[0.1em] mb-2 block"
          style={{ color: theme.textMuted }}
        >
          Interface Theme
        </label>
        <div className="flex gap-2 flex-wrap">
          {INTERFACE_THEMES.map((t) => (
            <button
              key={t.id}
              title={t.label}
              onClick={() => setInterfaceTheme(t.id)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-[4px] border transition-colors font-[var(--font-dm-mono)] text-[10px] uppercase tracking-[0.08em]"
              style={{
                background:
                  interfaceTheme === t.id ? t.panel : "transparent",
                borderColor:
                  interfaceTheme === t.id ? t.accent : theme.panelBorder,
                color:
                  interfaceTheme === t.id ? t.accent : theme.textMuted,
              }}
            >
              <span
                className="w-3 h-3 rounded-full border"
                style={{
                  background: t.panel,
                  borderColor: t.accent,
                }}
              />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Audio Engine ── */}
      <div
        className="mb-5 rounded-[4px] p-3"
        style={{
          background: theme.inputBg,
          border: `1px solid ${theme.panelBorder}`,
        }}
      >
        {/* Header row: label + gear config + on/off toggle. Audio is free and
            unlimited on every tier. */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <span
              className="font-[var(--font-barlow)] text-[11px] font-medium uppercase tracking-[0.1em]"
              style={{ color: theme.textMuted }}
            >
              Audio
            </span>
            {/* Gear button — opens preset configure panel */}
            <button
              onClick={() => setShowPresetConfig((v) => !v)}
              title="Add or remove sounds from the quick-access row"
              className="flex items-center gap-1 px-1.5 py-0.5 rounded-[2px] border transition-colors"
              style={
                showPresetConfig
                  ? { borderColor: theme.accent, color: theme.accent, background: theme.accent + "18" }
                  : { borderColor: theme.panelBorder, color: theme.textMuted, background: "transparent" }
              }
            >
              <svg width="9" height="9" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 10.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5zm5.86-1.54-.9-.52a5.1 5.1 0 0 0 0-1.48l.9-.52a.5.5 0 0 0 .18-.68l-1-1.73a.5.5 0 0 0-.68-.18l-.9.52a5.06 5.06 0 0 0-1.28-.74V3a.5.5 0 0 0-.5-.5h-2a.5.5 0 0 0-.5.5v1.03a5.06 5.06 0 0 0-1.28.74l-.9-.52a.5.5 0 0 0-.68.18l-1 1.73a.5.5 0 0 0 .18.68l.9.52a5.1 5.1 0 0 0 0 1.48l-.9.52a.5.5 0 0 0-.18.68l1 1.73c.14.24.44.32.68.18l.9-.52c.4.3.83.54 1.28.74V13a.5.5 0 0 0 .5.5h2a.5.5 0 0 0 .5-.5v-1.03c.45-.2.88-.44 1.28-.74l.9.52a.5.5 0 0 0 .68-.18l1-1.73a.5.5 0 0 0-.18-.68z"/>
              </svg>
              <span className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.08em]">
                Manage Sounds
              </span>
            </button>
          </div>
          {/* Toggle switch */}
          <button
            onClick={() => setAudioOn(!audioOn)}
            className="relative w-8 h-4 rounded-full transition-colors"
            style={{
              background: audioOn ? theme.accent : theme.panelBorder,
              cursor: "pointer",
            }}
          >
            <span
              className="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform"
              style={{ transform: audioOn ? "translateX(16px)" : "translateX(0)" }}
            />
          </button>
        </div>

        {/* Headphone advisory — the bilateral tone is a binaural beat that pans
            L↔R. Without the client's headphones it still plays, but through
            their speakers and without the binaural effect. Browsers can't
            reliably detect headphones, so this is a static reminder. */}
        <p
          className="mb-3 font-[var(--font-barlow)] text-[9px] leading-relaxed"
          style={{ color: theme.textMuted }}
        >
          🎧 Best with the client on headphones. Without them, the tone plays
          through their speakers — the left/right panning and binaural effect
          won&apos;t come through.
        </p>

        {/* Preset configure panel (collapsible) */}
        {showPresetConfig && (
          <div
            className="mb-3 rounded-[4px]"
            style={{ background: "#112240", border: `1px solid ${theme.panelBorder}` }}
          >
            {/* Scrollable sound list */}
            <div
              className="p-2.5"
              style={{
                maxHeight: 300,
                overflowY: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: `${theme.panelBorder} transparent`,
              }}
            >
              <p
                className="font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.1em] mb-2"
                style={{ color: theme.textMuted }}
              >
                Visible presets
              </p>

              {/* ── Built-in binaural presets ── */}
              <p
                className="font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.12em] mb-1.5"
                style={{ color: theme.textMuted }}
              >
                Built-In
              </p>
              {(["theta", "alpha", "delta"] as const).map((p) => {
                const isChecked = audioPresetIds.includes(p);
                return (
                  <label key={p} className="flex items-center gap-2 py-1 cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={isChecked}
                      onChange={() => {
                        const next = isChecked
                          ? audioPresetIds.filter((x) => x !== p)
                          : [...audioPresetIds, p];
                        if (isChecked && audioPreset === p) setAudioPreset("off");
                        onAudioPresetIdsChange?.(next);
                      }}
                    />
                    <span
                      className="w-3.5 h-3.5 rounded-[2px] border flex items-center justify-center shrink-0 transition-colors"
                      style={{
                        borderColor: isChecked ? theme.accent : theme.panelBorder,
                        background: isChecked ? theme.accent : "transparent",
                      }}
                    >
                      {isChecked && (
                        <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="#080C14" strokeWidth="2">
                          <polyline points="1.5,5 4,7.5 8.5,2" />
                        </svg>
                      )}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span
                        className="font-[var(--font-dm-mono)] text-[12px] uppercase tracking-[0.06em] block"
                        style={{ color: isChecked ? theme.textPrimary : theme.textMuted }}
                      >
                        {AUDIO_PRESETS[p].label}
                      </span>
                      <span
                        className="font-[var(--font-barlow)] text-[10px] block"
                        style={{ color: "#C9A84C" }}
                      >
                        {AUDIO_PRESETS[p].desc}
                      </span>
                    </span>
                    <span
                      className="font-[var(--font-dm-mono)] text-[10px] ml-auto shrink-0"
                      style={{ color: theme.textMuted, opacity: 0.7 }}
                    >
                      {AUDIO_PRESETS[p].carrier} Hz
                    </span>
                  </label>
                );
              })}

              {/* ── Library sounds grouped by category ── */}
              {(["Theta", "Alpha", "Delta", "Gamma", "Noise", "Solfeggio", "Tuning"] as const).map((cat) => {
                const sounds = SOUND_LIBRARY.filter((s) => s.category === cat);
                if (!sounds.length) return null;
                return (
                  <div key={cat}>
                    <p
                      className="font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.12em] mt-3 mb-1.5"
                      style={{ color: theme.textMuted }}
                    >
                      {cat}
                    </p>
                    {sounds.map((sound) => {
                      const isChecked = audioPresetIds.includes(sound.id);
                      return (
                        <label key={sound.id} className="flex items-center gap-2 py-1 cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={isChecked}
                            onChange={() => {
                              const next = isChecked
                                ? audioPresetIds.filter((x) => x !== sound.id)
                                : [...audioPresetIds, sound.id];
                              if (isChecked && audioPreset === sound.id) setAudioPreset("off");
                              onAudioPresetIdsChange?.(next);
                            }}
                          />
                          <span
                            className="w-3.5 h-3.5 rounded-[2px] border flex items-center justify-center shrink-0 transition-colors"
                            style={{
                              borderColor: isChecked ? theme.accent : theme.panelBorder,
                              background: isChecked ? theme.accent : "transparent",
                            }}
                          >
                            {isChecked && (
                              <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="#080C14" strokeWidth="2">
                                <polyline points="1.5,5 4,7.5 8.5,2" />
                              </svg>
                            )}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span
                              className="font-[var(--font-dm-mono)] text-[11px] block truncate"
                              style={{ color: isChecked ? theme.textPrimary : theme.textMuted }}
                            >
                              {sound.name
                                .replace(" Binaural Beat", "")
                                .replace(" Isochronic Tone", " Iso")
                                .replace(" — ", " ")
                                .slice(0, 30)}
                            </span>
                            <span
                              className="font-[var(--font-barlow)] text-[10px] block truncate"
                              style={{ color: "#C9A84C" }}
                            >
                              {sound.purpose}
                            </span>
                          </span>
                          <span
                            className="font-[var(--font-dm-mono)] text-[10px] ml-auto shrink-0"
                            style={{ color: theme.textMuted, opacity: 0.7 }}
                          >
                            {sound.carrierHz ? `${sound.carrierHz} Hz` : sound.type}
                            {sound.beatHz ? ` · ${sound.beatHz} Hz` : ""}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}

              {/* ── My Files ── */}
              {userAudioFiles.length > 0 && (
                <div>
                  <p
                    className="font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.12em] mt-3 mb-1.5"
                    style={{ color: theme.textMuted }}
                  >
                    My Files
                  </p>
                  {userAudioFiles.map((file) => {
                    const presetId = `custom:${file.id}`;
                    const isChecked = audioPresetIds.includes(presetId);
                    return (
                      <label key={file.id} className="flex items-center gap-2 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={isChecked}
                          onChange={() => {
                            const next = isChecked
                              ? audioPresetIds.filter((x) => x !== presetId)
                              : [...audioPresetIds, presetId];
                            if (isChecked && audioPreset === presetId) setAudioPreset("off");
                            onAudioPresetIdsChange?.(next);
                          }}
                        />
                        <span
                          className="w-3.5 h-3.5 rounded-[2px] border flex items-center justify-center shrink-0 transition-colors"
                          style={{
                            borderColor: isChecked ? theme.accent : theme.panelBorder,
                            background: isChecked ? theme.accent : "transparent",
                          }}
                        >
                          {isChecked && (
                            <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="#080C14" strokeWidth="2">
                              <polyline points="1.5,5 4,7.5 8.5,2" />
                            </svg>
                          )}
                        </span>
                        <span
                          className="font-[var(--font-dm-mono)] text-[11px] flex-1 min-w-0 truncate"
                          style={{ color: isChecked ? theme.textPrimary : theme.textMuted }}
                        >
                          {file.display_name.slice(0, 28)}
                        </span>
                        <span
                          className="font-[var(--font-dm-mono)] text-[10px] ml-auto shrink-0"
                          style={{ color: theme.textMuted, opacity: 0.7 }}
                        >
                          {file.file_type.toUpperCase()}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Done button — outside scroll */}
            <div className="px-2.5 pb-2.5">
              <button
                onClick={() => setShowPresetConfig(false)}
                className="w-full font-[var(--font-barlow)] text-[9px] uppercase tracking-[0.1em] py-1 rounded-[2px] border transition-colors"
                style={{ borderColor: theme.panelBorder, color: theme.textMuted }}
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* Preset selector — filtered by audioPresetIds */}
        <div className="flex gap-1 mb-3 flex-wrap">
          {([...audioPresetIds, "off"]).map((p) => {
            const { main, sub } = getPresetLabel(p, userAudioFiles);
            return (
              <button
                key={p}
                onClick={() => setAudioPreset(p)}
                disabled={!audioOn}
                className="py-1.5 px-1 rounded-[4px] border flex flex-col items-center gap-0.5 transition-colors disabled:opacity-30"
                style={{
                  borderColor: audioPreset === p ? theme.accent : theme.panelBorder,
                  color: audioPreset === p ? theme.accent : theme.textMuted,
                  background: audioPreset === p ? theme.accent + "18" : "transparent",
                  minWidth: 44,
                  maxWidth: 68,
                }}
              >
                <span
                  className="font-[var(--font-dm-mono)] text-[9px] uppercase tracking-[0.06em] leading-tight w-full text-center"
                  style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {main}
                </span>
                {sub && (
                  <span
                    className="font-[var(--font-dm-mono)] text-[8px] w-full text-center"
                    style={{
                      color: audioPreset === p ? theme.accent : theme.textMuted,
                      opacity: 0.75,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {sub}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Motion tempo + beat freq readout */}
        <div className="flex items-center justify-between mb-3">
          <span
            className="font-[var(--font-dm-mono)] text-[9px]"
            style={{ color: theme.textMuted }}
          >
            Motion&nbsp;
            <span style={{ color: theme.accent }}>{hz.toFixed(1)} Hz</span>
            <span style={{ opacity: 0.5 }}> · {(1 / hz).toFixed(2)}s cycle</span>
          </span>
          {audioOn && audioPreset !== "off" && (
            <span
              className="font-[var(--font-dm-mono)] text-[9px]"
              style={{ color: theme.textMuted }}
            >
              {getAudioDesc(audioPreset, userAudioFiles)}
            </span>
          )}
        </div>

        {/* Volume slider */}
        <div className="flex items-center gap-2">
          <span
            className="font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.08em] shrink-0"
            style={{ color: theme.textMuted }}
          >
            Vol
          </span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={audioVolume}
            onChange={(e) => setAudioVolume(parseFloat(e.target.value))}
            disabled={!audioOn}
            className="flex-1 disabled:opacity-30"
          />
          <span
            className="font-[var(--font-dm-mono)] text-[10px] w-7 text-right shrink-0"
            style={{ color: theme.accent }}
          >
            {Math.round(audioVolume * 100)}%
          </span>
        </div>
      </div>

      {/* ── Haptics (controller rumble) ──
          Mirrors the Audio block above: header row with on/off toggle, status
          hint, intensity slider. Browser quirk: the connected controller stays
          invisible to navigator.getGamepads() until the user presses a button
          on it — the status badge nudges with "Press any button on your
          controller" until that happens. */}
      <div
        className="mb-5 rounded-[4px] p-3"
        style={{
          background: theme.inputBg,
          border: `1px solid ${theme.panelBorder}`,
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className="font-[var(--font-barlow)] text-[11px] font-medium uppercase tracking-[0.1em]"
              style={{ color: theme.textMuted }}
            >
              Haptics
            </span>
            <HapticsStatus
              status={hapticsStatus}
              controllerLabel={hapticsControllerLabel}
              textMuted={theme.textMuted}
              accent={theme.accent}
            />
          </div>
          <button
            onClick={() => setHapticsOn(!hapticsOn)}
            className="relative w-8 h-4 rounded-full transition-colors"
            style={{
              background: hapticsOn ? theme.accent : theme.panelBorder,
              cursor: "pointer",
            }}
          >
            <span
              className="absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white transition-transform"
              style={{ transform: hapticsOn ? "translateX(16px)" : "translateX(0)" }}
            />
          </button>
        </div>

        {/* Intensity slider — disabled when off or locked */}
        <div className="flex items-center gap-2">
          <span
            className="font-[var(--font-barlow)] text-[10px] uppercase tracking-[0.08em] shrink-0"
            style={{ color: theme.textMuted }}
          >
            Pulse
          </span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={hapticsIntensity}
            onChange={(e) => setHapticsIntensity(parseFloat(e.target.value))}
            disabled={!hapticsOn}
            className="flex-1 disabled:opacity-30"
          />
          <span
            className="font-[var(--font-dm-mono)] text-[10px] w-7 text-right shrink-0"
            style={{ color: theme.accent }}
          >
            {Math.round(hapticsIntensity * 100)}%
          </span>
        </div>

        {/* Usage tips — field-tested defaults the therapist may want to
            consult while dialing in. Only shown when the section is active
            (toggle on) so it surfaces in-context and doesn't clutter the
            panel when haptics is off. */}
        {hapticsOn && (
          <p
            className="mt-2 font-[var(--font-barlow)] text-[9px] leading-relaxed"
            style={{ color: theme.textMuted }}
          >
            For best feel: hold the controller higher, near the triggers.
            Try ~80% intensity at 0.8 Hz to start. Xbox controllers may
            buzz slightly differently from DualSense.
          </p>
        )}
      </div>

    </div>
  );
}
